/**
 * Postback API (FS-174) — operator settings page.
 *
 * Surface for `/api/v1/postback-api`:
 *
 *   1. Inbound URL card — the POST endpoint operators give their partners.
 *   2. Signing instructions card — canonical message shape + curl example.
 *   3. Generate-secret form + copy-once modal — the plain secret is displayed
 *      exactly once and disappears from the DOM when the modal is closed.
 *   4. Secrets table — preview-only listing + revoke action.
 *   5. Events table — last 50 audit rows with status chips.
 *
 * Brand: emerald → sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	buildCurlSample,
	createSecret,
	deriveEventStatus,
	EMPTY_SECRET_DRAFT,
	eventStatusBadgeClass,
	EVENT_STATUS_LABELS,
	formatRevenueCents,
	formatTimestamp,
	listEvents,
	listSecrets,
	postbackApiQueryKeys,
	revokeSecret,
	secretDraftSchema,
	type EventView,
	type NewSecretView,
	type SecretDraft,
	type SecretView,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const CODE =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-xs text-zinc-800";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PostbackApiPageProps {
	/** Base URL partners should hit. */
	inboundBaseUrl?: string;
}

export function PostbackApiPage({ inboundBaseUrl }: PostbackApiPageProps) {
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<SecretDraft>(EMPTY_SECRET_DRAFT);
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [newSecret, setNewSecret] = useState<NewSecretView | null>(null);

	const secretsQuery = useQuery({
		queryKey: postbackApiQueryKeys.secrets(),
		queryFn: listSecrets,
	});

	const eventsQuery = useQuery({
		queryKey: postbackApiQueryKeys.events(50),
		queryFn: () => listEvents(50),
	});

	const createMutation = useMutation({
		mutationFn: (name: string) => createSecret(name),
		onSuccess: (row: NewSecretView) => {
			setNewSecret(row);
			setDraft(EMPTY_SECRET_DRAFT);
			setDraftErrors({});
			toast.success(`Secret "${row.name}" generated.`);
			queryClient.invalidateQueries({
				queryKey: postbackApiQueryKeys.secrets(),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to generate secret.");
		},
	});

	const revokeMutation = useMutation({
		mutationFn: (id: string) => revokeSecret(id),
		onSuccess: () => {
			toast.success("Secret revoked.");
			queryClient.invalidateQueries({
				queryKey: postbackApiQueryKeys.secrets(),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to revoke secret.");
		},
	});

	function submitDraft() {
		const parsed = secretDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data.name);
	}

	const base = useMemo(() => (inboundBaseUrl ?? "https://api.example.com").replace(/\/+$/, ""), [inboundBaseUrl]);
	const inboundUrl = `${base}/api/v1/postback-api/v1/conversion`;
	const curl = useMemo(() => buildCurlSample({ baseUrl: base }), [base]);

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 pb-24 pt-8">
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Postback API
				</h1>
				<p className="text-sm text-zinc-600">
					Accept signed JSON postbacks for conversions. HMAC-SHA256 over
					`&lt;timestamp&gt;.&lt;body&gt;` with a per-tenant secret. Timestamp skew
					tolerance is 5 minutes each direction; replays and duplicate
					(click_id, goal) pairs are rejected automatically.
				</p>
			</header>

			{/* Inbound URL */}
			<section className={`${CARD} p-5`} aria-labelledby="url-hdr">
				<h2 id="url-hdr" className="text-base font-semibold text-zinc-900">
					Inbound URL
				</h2>
				<p className={`${HINT} mt-1`}>
					Give this URL to the partner. Every request must be signed.
				</p>
				<div className="mt-3 flex flex-wrap items-center gap-2">
					<code className={`${CODE} break-all`}>{inboundUrl}</code>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => {
							navigator.clipboard
								?.writeText(inboundUrl)
								.then(() => toast.success("Copied."))
								.catch(() => toast.error("Copy failed."));
						}}
					>
						Copy
					</button>
				</div>
			</section>

			{/* Signing instructions */}
			<section className={`${CARD} p-5`} aria-labelledby="sig-hdr">
				<h2 id="sig-hdr" className="text-base font-semibold text-zinc-900">
					How to sign
				</h2>
				<ul className={`${HINT} mt-2 flex list-disc flex-col gap-1 pl-4`}>
					<li>
						Set <code className={CODE}>X-Trackerity-Timestamp</code> to the
						current UNIX seconds.
					</li>
					<li>
						Compute the HMAC-SHA256 of
						{" "}
						<code className={CODE}>{"<timestamp>.<raw_body>"}</code>
						{" "}
						using your postback secret.
					</li>
					<li>
						Send the lowercase hex digest in
						{" "}
						<code className={CODE}>X-Trackerity-Signature: sha256=&lt;hex&gt;</code>.
					</li>
					<li>
						Dedup key is <code className={CODE}>click_id|goal</code> (case-insensitive).
					</li>
				</ul>
				<pre className="mt-3 overflow-x-auto rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-800">
					<code>{curl}</code>
				</pre>
			</section>

			{/* Generate new secret */}
			<section className={`${CARD} p-5`} aria-labelledby="gen-hdr">
				<h2 id="gen-hdr" className="text-base font-semibold text-zinc-900">
					Generate a new secret
				</h2>
				<p className={`${HINT} mt-1`}>
					Plain secrets are shown exactly once. Copy and store securely — we
					keep only the hash.
				</p>
				<div className="mt-3 flex flex-col gap-2 md:flex-row md:items-end">
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="name" className={LABEL}>
							Name
						</label>
						<input
							id="name"
							className={INPUT}
							value={draft.name}
							onChange={(e) => setDraft({ name: e.target.value })}
							placeholder="Partner ACME"
						/>
						{draftErrors.name && <p className={ERROR_TEXT}>{draftErrors.name}</p>}
					</div>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={submitDraft}
						disabled={createMutation.isPending}
					>
						{createMutation.isPending ? "Generating\u2026" : "Generate"}
					</button>
				</div>
			</section>

			{/* Secrets table */}
			<section className={`${CARD} p-5`} aria-labelledby="sec-hdr">
				<div className="flex items-center justify-between">
					<h2 id="sec-hdr" className="text-base font-semibold text-zinc-900">
						Secrets
					</h2>
					<span className={HINT}>{secretsQuery.data?.length ?? 0} total</span>
				</div>
				<div className="mt-3 overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-3 py-2">Name</th>
								<th className="px-3 py-2">Preview</th>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2">Created</th>
								<th className="px-3 py-2" />
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{(secretsQuery.data ?? []).map((row) => (
								<SecretRow
									key={row.id}
									row={row}
									onRevoke={() => revokeMutation.mutate(row.id)}
									revoking={revokeMutation.isPending}
								/>
							))}
							{!secretsQuery.isLoading && (secretsQuery.data ?? []).length === 0 && (
								<tr>
									<td colSpan={5} className="px-3 py-6 text-center text-zinc-500">
										No secrets yet.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>

			{/* Events */}
			<section className={`${CARD} p-5`} aria-labelledby="evt-hdr">
				<div className="flex items-center justify-between">
					<h2 id="evt-hdr" className="text-base font-semibold text-zinc-900">
						Recent postback events
					</h2>
					<span className={HINT}>{eventsQuery.data?.length ?? 0} rows</span>
				</div>
				<div className="mt-3 overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2">Click</th>
								<th className="px-3 py-2">Goal</th>
								<th className="px-3 py-2">Revenue</th>
								<th className="px-3 py-2">At</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{(eventsQuery.data ?? []).map((row) => (
								<EventRow key={row.id} row={row} />
							))}
							{!eventsQuery.isLoading && (eventsQuery.data ?? []).length === 0 && (
								<tr>
									<td colSpan={5} className="px-3 py-6 text-center text-zinc-500">
										No events yet.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>

			{/* Copy-once modal */}
			{newSecret && (
				<CopyOnceModal
					secret={newSecret}
					onClose={() => setNewSecret(null)}
				/>
			)}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SecretRow({
	row,
	onRevoke,
	revoking,
}: {
	row: SecretView;
	onRevoke: () => void;
	revoking: boolean;
}) {
	return (
		<tr className="align-top">
			<td className="px-3 py-2 text-zinc-900">{row.name}</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs">{row.secret_preview}</code>
			</td>
			<td className="px-3 py-2">
				<span
					className={`inline-flex rounded-[6px] border px-2 py-0.5 text-xs ${
						row.active
							? "bg-emerald-50 text-emerald-700 border-emerald-200"
							: "bg-zinc-50 text-zinc-600 border-zinc-200"
					}`}
				>
					{row.active ? "Active" : "Revoked"}
				</span>
			</td>
			<td className="px-3 py-2 text-zinc-500 text-xs">
				{formatTimestamp(row.created_at)}
			</td>
			<td className="px-3 py-2 text-right">
				{row.active && (
					<button
						type="button"
						className={BTN_DANGER}
						onClick={onRevoke}
						disabled={revoking}
					>
						Revoke
					</button>
				)}
			</td>
		</tr>
	);
}

function EventRow({ row }: { row: EventView }) {
	const chip = deriveEventStatus(row);
	return (
		<tr className="align-top">
			<td className="px-3 py-2">
				<span
					className={`inline-flex rounded-[6px] border px-2 py-0.5 text-xs ${eventStatusBadgeClass(
						chip,
					)}`}
				>
					{EVENT_STATUS_LABELS[chip]}
				</span>
			</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs">{row.click_id}</code>
			</td>
			<td className="px-3 py-2 text-zinc-700">{row.goal}</td>
			<td className="px-3 py-2 text-zinc-700">
				{formatRevenueCents(row.revenue_cents)}
			</td>
			<td className="px-3 py-2 text-zinc-500 text-xs">
				{formatTimestamp(row.created_at)}
			</td>
		</tr>
	);
}

function CopyOnceModal({
	secret,
	onClose,
}: {
	secret: NewSecretView;
	onClose: () => void;
}) {
	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="copy-once-hdr"
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
		>
			<div className={`${CARD} flex w-full max-w-xl flex-col gap-4 p-6`}>
				<div className="flex items-start justify-between gap-4">
					<div>
						<h3 id="copy-once-hdr" className="text-lg font-semibold text-zinc-900">
							Copy your secret now
						</h3>
						<p className={`${HINT} mt-1`}>
							This value will not be shown again. If you lose it, revoke this
							secret and generate a new one.
						</p>
					</div>
				</div>
				<div className="flex flex-col gap-2">
					<label className={LABEL}>Name</label>
					<code className={CODE}>{secret.name}</code>
				</div>
				<div className="flex flex-col gap-2">
					<label className={LABEL}>Plain secret</label>
					<code className={`${CODE} break-all`}>{secret.plain}</code>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => {
							navigator.clipboard
								?.writeText(secret.plain)
								.then(() => toast.success("Secret copied."))
								.catch(() => toast.error("Copy failed."));
						}}
					>
						Copy secret
					</button>
				</div>
				<div className="flex justify-end gap-2">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={onClose}
					>
						I have saved it
					</button>
				</div>
			</div>
		</div>
	);
}

export default PostbackApiPage;
